import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import { QuotePremiumSummaryDisplay } from "@/components/quote/QuotePremiumSummary";
import { useQuoteWizardContext } from "../QuoteWizardContext";
import { getCoverageLabel } from "../constants";
import { useResolveGeo } from "@/hooks/useQuoteValidation";
import { pluralize } from "@/lib/pluralize";

export function Step8QuoteSummary() {
  const { quoteData, premiumSummary, currentPremiumSummary } = useQuoteWizardContext();
  
  const entityZip = quoteData.selectedEntity?.zipCode;
  const entityState = quoteData.selectedEntity?.state || "CA";
  const { data: geoContext } = useResolveGeo(entityState, entityZip, quoteData.effectiveDate || undefined);
  
  const ratingAreaLabel = quoteData.confirmedRatingArea?.label 
    || (geoContext?.ratingAreaId ? `AREA ${geoContext.ratingAreaId}` : null);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Quote Summary</h3>
        <p className="text-muted-foreground">
          Review your selected plans and coverage details
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="shadow-sm">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-base">Quote Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 py-2 px-4 text-sm">
            <div className="flex justify-between">
              <span className="font-medium text-muted-foreground">Entity:</span>
              <span>{quoteData.selectedEntity?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-muted-foreground">Type:</span>
              <span>{quoteData.clientType}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-muted-foreground">Effective Date:</span>
              <span>{quoteData.effectiveDate ? format(quoteData.effectiveDate, "PPP") : "Not set"}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-muted-foreground">Employees:</span>
              <span>{quoteData.selectedEmployees.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-muted-foreground">Coverage Types:</span>
              <span>{quoteData.coverageTypes.length}</span>
            </div>
            {quoteData.coverageTypes.includes("health") && ratingAreaLabel && (
              <div className="flex justify-between">
                <span className="font-medium text-muted-foreground">Rate Area:</span>
                <span>{ratingAreaLabel}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-base">Selected Plans Summary</CardTitle>
          </CardHeader>
          <CardContent className="py-2 px-4">
            <div className="space-y-2">
              {quoteData.coverageTypes.map((type) => (
                <div key={type} className="flex items-center justify-between border-l-2 border-primary pl-2 py-1">
                  <span className="font-medium text-sm">{getCoverageLabel(type)}</span>
                  <span className="text-sm text-muted-foreground">
                    {quoteData.selectedPlans[type]?.length || 0}{" "}
                    {pluralize(quoteData.selectedPlans[type]?.length || 0, "plan")}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Plan Options & Premiums */}
      <QuotePremiumSummaryDisplay
        premiumSummary={premiumSummary}
        currentPremiumSummary={currentPremiumSummary}
        compact={true}
        showEmployerSplit={false}
      />
    </div>
  );
}
